import React, { Fragment, useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import ReCAPTCHA from 'react-google-recaptcha';
import { values, keys, assign } from 'lodash-es';

import SimpleCheckbox from '../../../../../components/Checkbox/SimpleCheckbox';
import helpers from '../../../../../utils/helpers';
import Button from '../../../../../components/Buttons/Button';

const labelTextTemplate = (
  <span className="label-with-links">
    By checking this box, you agree to Apptopia&apos;s <a target="_blank" rel="noopener noreferrer" href="https://apptopia.com/privacy">Privacy Notice</a>, <a target="_blank" rel="noopener noreferrer" href="https://apptopia.com/pricing">Pricing Policy</a>, <a target="_blank" rel="noopener noreferrer" href="https://apptopia.com/terms">Terms of Service</a>, <a target="_blank" rel="noopener noreferrer" href="https://apptopia.com/data-usage">Data Usage</a>, and to receive the occasional product and marketing email from us.
  </span>
);

const UserReportFormMobileModal = ({
  title,
  sitekey,
  customErrors = {},
  isMobileMode,
  isSubmitting,
  submitStarted,
  className,
  onStepBack,
  onSubmitClick,
  windowY,
}) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    jobFunction: '',
    checked: false,
  });
  const [errors, setErrors] = useState({
    firstName: '',
    lastName: '',
    email: '',
    jobFunction: '',
    checked: '',
    ...customErrors,
  });

  const recaptchaRef = useRef(null);
  const {
    firstName,
    lastName,
    email,
    jobFunction,
    checked,
  } = formData;

  const handlePreventScroll = (action) => {
    if (isMobileMode) {
      document.documentElement.style.top = `-${windowY}px`;
      document.documentElement.classList[action]('pos-fixed');
      document.documentElement.classList[action]('w-100');
    }
  };

  const scrollToY = () => {
    if (isMobileMode) {
      document.documentElement.style.top = '0px';
      window.scrollY = window.scrollTo(0, windowY);
    }
  };

  useEffect(() => {
    handlePreventScroll('add');
    return () => {
      handlePreventScroll('remove');
      scrollToY();
    };
  }, []);

  useEffect(() => {
    setErrors(prevErrors => ({ ...prevErrors, ...customErrors }));
  }, [customErrors]);

  const validateError = (field) => {
    const key = keys(field)[0];
    const value = values(field)[0];
    switch (key) {
      case 'firstName':
        return { firstName: !value.trim() ? 'Please input valid First name' : '' };
      case 'lastName':
        return { lastName: !value.trim() ? 'Please input valid Last name' : '' };
      case 'jobFunction':
        return { jobFunction: !value.trim() ? 'Please input valid Job title' : '' };
      case 'email':
        return { email: (!value || !helpers.validateEmail(value) || value.includes('+')) ? 'Please enter a valid email address' : '' };
      case 'checked':
        return { checked: !value ? 'Please accept Terms of Service' : '' };
      default:
        return {};
    }
  };

  const validateForm = () => {
    const errorsState = { ...errors };

    assign(errorsState, validateError({ firstName }));
    assign(errorsState, validateError({ lastName }));
    assign(errorsState, validateError({ jobFunction }));
    assign(errorsState, validateError({ email }));
    assign(errorsState, validateError({ checked }));

    setErrors(errorsState);
  };

  const isErrorsPresent = () => (
    errors.email.length > 0
      || errors.firstName.length > 0
      || errors.lastName.length > 0
      || errors.jobFunction.length > 0
      || errors.checked.length > 0
  );

  const isFormFilled = () => (
    firstName.length > 0
      && lastName.length > 0
      && email.length > 0
      && jobFunction.length > 0
      && checked
  );

  const submitDisabled = () => !isFormFilled() || isErrorsPresent();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!submitDisabled() && !isSubmitting) {
      submitStarted();
      recaptchaRef.current.reset();
      recaptchaRef.current.executeAsync().then((token) => {
        validateForm();
        onSubmitClick(
          token,
          {
            user: {
              first_name: firstName,
              last_name: lastName,
              email,
              job_function: jobFunction,
            },
          },
          isErrorsPresent(),
        );
      });
    }
  };

  const handleFieldChange = (field) => {
    setFormData({ ...formData, ...field });
    setErrors({ ...errors, ...validateError(field) });
  };

  const handleGoBack = () => {
    if (onStepBack) onStepBack();
  };

  return (
    <Fragment>
      <div className={cx('d-flex flex-column flex-grow user-report-form-box', className)}>
        <div className="flex-no-shrink d-flex flex-wrap flex-column w-100 user-report-form-header">
          {isMobileMode && (
            <button
              type="button"
              className={cx('btn btn-outline form-back-button-mobile', { disabled: isSubmitting })}
              onClick={handleGoBack}
            >
              <span className="hicon-angle-left" />
            </button>
          )}
          <h2 className="report-analysis-search-header user-form-header text-16 text-uppercase m-b-mdl">{title}</h2>
        </div>
        <form
          className="user-report-form"
          id="user-report-form"
          onSubmit={handleSubmit}
        >
          <div className="row m-a-0 p-a-0 field">
            <div className="col-md-12 p-a-0">
              <fieldset className={cx('form-group pos-rel mb-sm', { 'has-error': Boolean(errors.firstName) })}>
                <input
                  className="form-control"
                  placeholder="First Name"
                  type="text"
                  value={firstName}
                  name="firstName"
                  onChange={e => handleFieldChange({ firstName: e.target.value })}
                />
                {errors.firstName &&
                <div className="error-message">
                  {errors.firstName}
                </div>
                }
              </fieldset>
            </div>
          </div>
          <div className="row m-a-0 p-a-0 field w-100">
            <div className="col-md-12 p-a-0">
              <fieldset className={cx('form-group pos-rel mb-sm', { 'has-error': Boolean(errors.lastName) })}>
                <input
                  className="form-control"
                  placeholder="Last Name"
                  type="text"
                  value={lastName}
                  name="lastName"
                  onChange={e => handleFieldChange({ lastName: e.target.value })}
                />
                {errors.lastName &&
                <div className="error-message">
                  {errors.lastName}
                </div>
                }
              </fieldset>
            </div>
          </div>
          <div className="row m-a-0 p-a-0 field w-100 email">
            <div className="col-md-12 p-a-0">
              <fieldset className={cx('form-group pos-rel', { 'has-error': Boolean(errors.email) })}>
                <input
                  className="form-control"
                  placeholder="Business Email"
                  type="text"
                  value={email}
                  name="email"
                  onChange={e => handleFieldChange({ email: e.target.value })}
                />
                {errors.email &&
                <div className="error-message">
                  {errors.email}
                </div>
                }
              </fieldset>
            </div>
          </div>
          <div className="row m-a-0 p-a-0 field w-100">
            <div className="col-md-12 p-a-0">
              <fieldset className={cx('form-group pos-rel mb-sm dropdown-job', { 'has-error': Boolean(errors.jobFunction) })}>
                <input
                  className="form-control"
                  placeholder="Job Title"
                  type="text"
                  value={jobFunction}
                  name="jobTitle"
                  onChange={e => handleFieldChange({ jobFunction: e.target.value })}
                />
                {errors.jobFunction &&
                  <div className="error-message">
                    {errors.jobFunction}
                  </div>
                }
              </fieldset>
            </div>
          </div>
          <div className="row m-a-0 p-a-0 field w-100 confirm-checkbox">
            <div className="col-md-12 p-a-0 justify-content-center align-items-center flex-column">
              <SimpleCheckbox
                id="agreement-check"
                label={labelTextTemplate}
                checked={checked}
                gap="20px"
                onChange={active => handleFieldChange({ checked: active })}
              />
              {errors.checked &&
                <div className="text-red text-12 p-t-xxs">
                  {errors.checked}
                </div>
              }
            </div>
          </div>
          <div className="row m-a-0 p-a-0 w-100">
            <div className="d-flex col-md-12 p-a-0 justify-content-center align-items-center">
              {!isMobileMode && (
                <Button
                  variant="secondary"
                  size="lg"
                  disabled={isSubmitting}
                  className="mr-xl form-back-button"
                  onClick={handleGoBack}
                >
                Back
                </Button>
              )}
              <Button
                type="submit"
                size="lg"
                loading={isSubmitting}
                disabled={submitDisabled()}
                className="report-analysis-submit-btn"
              >
                {isMobileMode ? 'Get My Free Report' : 'Submit'}
              </Button>
            </div>
          </div>
        </form>
      </div>
      <ReCAPTCHA
        ref={recaptchaRef}
        size="invisible"
        sitekey={sitekey}
      />
    </Fragment>
  );
};

UserReportFormMobileModal.propTypes = {
  onStepBack: PropTypes.func,
  onSubmitClick: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  sitekey: PropTypes.string.isRequired,
  submitStarted: PropTypes.func.isRequired,
  customErrors: PropTypes.object,
  title: PropTypes.string.isRequired,
  className: PropTypes.string,
  isMobileMode: PropTypes.bool,
  windowY: PropTypes.number,
};

export default UserReportFormMobileModal;
